/**
 * HeterosisSection — renders the MPH / BPH / SH heterosis tables for
 * Diallel (Methods 1-4) and Line × Tester analyses.
 *
 * Reads from `data.heterosis` which is attached server-side by
 * /app/backend/heterosis.py. Color-coded significance markers:
 *    ** (1%)  →  bold deep-green
 *    *  (5%)  →  green
 *    blank   →  neutral
 *
 * Niranjan, Feb 2026 — Phase A part 2 of heterosis-and-genmean scope.
 */
import React, { useState } from 'react';
import { Pin, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

const SIG_STYLES = {
  '**': { color: '#0B5132', fontWeight: 700, background: '#D6F4E1' },  // deep green / 1%
  '*':  { color: '#2D8B57', fontWeight: 600, background: '#EAF7EE' },  // green / 5%
  '':   { color: '#3C4043', fontWeight: 400, background: 'transparent' },
};

function fmtPct(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}%`;
}

function fmtNum(v, digits = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return Number(v).toFixed(digits);
}

function HeterosisRow({ row, columns }) {
  return (
    <tr className="hover:bg-[#F8FBF8] transition-colors">
      <td className="px-3 py-2 font-mono text-[12px] text-[#1F4E2E] font-medium">
        {row.Cross}
      </td>
      <td className="px-3 py-2 text-center font-mono text-[12px]">{fmtNum(row.F1_mean)}</td>
      {columns.includes('parents') && (
        <>
          <td className="px-3 py-2 text-center font-mono text-[12px] text-[#6B8F71]">{fmtNum(row.P1_mean)}</td>
          <td className="px-3 py-2 text-center font-mono text-[12px] text-[#6B8F71]">{fmtNum(row.P2_mean)}</td>
        </>
      )}
      {columns.includes('mph') && (
        <td
          className="px-3 py-2 text-center font-mono text-[12px]"
          style={SIG_STYLES[row.MPH_sig || '']}
          data-testid={`het-mph-${row.Cross}`}
        >
          {fmtPct(row.MPH_pct)}{row.MPH_sig ? <sup className="ml-0.5">{row.MPH_sig}</sup> : null}
        </td>
      )}
      {columns.includes('bph') && (
        <td
          className="px-3 py-2 text-center font-mono text-[12px]"
          style={SIG_STYLES[row.BPH_sig || '']}
          data-testid={`het-bph-${row.Cross}`}
        >
          {fmtPct(row.BPH_pct)}{row.BPH_sig ? <sup className="ml-0.5">{row.BPH_sig}</sup> : null}
        </td>
      )}
      {columns.includes('sh') && (
        <td
          className="px-3 py-2 text-center font-mono text-[12px]"
          style={SIG_STYLES[row.SH_sig || '']}
          data-testid={`het-sh-${row.Cross}`}
        >
          {fmtPct(row.SH_pct)}{row.SH_sig ? <sup className="ml-0.5">{row.SH_sig}</sup> : null}
        </td>
      )}
    </tr>
  );
}

function TopCrossesCard({ title, rows, metric, accent }) {
  if (!rows || !rows.length) return null;
  return (
    <div
      className="rounded-xl border border-[#E0EDE0] bg-white p-4"
      data-testid={`top-${metric}`}
    >
      <h5 className="text-[13px] font-bold text-[#1F4E2E] mb-3" style={{ borderLeft: `3px solid ${accent}`, paddingLeft: 10 }}>
        {title}
      </h5>
      <ol className="space-y-1.5 text-[12px]">
        {rows.map((r, i) => (
          <li key={r.Cross} className="flex items-start gap-2">
            <span className="font-bold text-[#6B8F71] w-4 shrink-0">{i + 1}.</span>
            <span className="font-mono text-[#1F4E2E]">{r.Cross}</span>
            <span className="ml-auto font-mono font-semibold" style={{ color: accent }}>
              {fmtPct(r[`${metric.toUpperCase()}_pct`])}
              {r[`${metric.toUpperCase()}_sig`] && (
                <sup className="ml-0.5">{r[`${metric.toUpperCase()}_sig`]}</sup>
              )}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function PinCandidateButton({ candidate, analysisId, analysisType, trait }) {
  const [state, setState] = useState('idle'); // 'idle' | 'pinning' | 'pinned'

  const handlePin = async () => {
    if (state !== 'idle') return;
    setState('pinning');
    try {
      await axios.post(
        `${API}/promotion-list`,
        {
          cross: candidate.Cross,
          analysis_id: analysisId,
          analysis_type: analysisType,
          trait,
          f1_mean: candidate.F1_mean,
          gca_sum: candidate.GCA_sum,
          sca: candidate.SCA,
          mph_pct: candidate.MPH_pct,
          bph_pct: candidate.BPH_pct,
          sh_pct: candidate.SH_pct,
          top_score: candidate.top_score,
        },
        { withCredentials: true },
      );
      setState('pinned');
      toast.success(`Pinned ${candidate.Cross} to promotion list.`);
    } catch (e) {
      setState('idle');
      toast.error(e?.response?.data?.detail || 'Could not pin — try again.');
    }
  };

  return (
    <button
      type="button"
      onClick={handlePin}
      disabled={state !== 'idle'}
      data-testid={`pin-candidate-${candidate.Cross}`}
      className="rounded-md font-body transition-all"
      style={{
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.04em',
        padding: '3px 9px',
        border: '1px solid',
        borderColor: state === 'pinned' ? '#0B5132' : '#7C3AED',
        background: state === 'pinned' ? '#D6F4E1' : 'white',
        color: state === 'pinned' ? '#0B5132' : '#7C3AED',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        opacity: state === 'pinning' ? 0.6 : 1,
        cursor: state === 'idle' ? 'pointer' : 'default',
      }}
      title={state === 'pinned' ? 'Saved to your promotion list' : 'Save to promotion list'}
    >
      {state === 'pinning' && <Loader2 size={11} className="animate-spin" />}
      {state === 'pinned' && <Check size={11} />}
      {state === 'idle' && <Pin size={11} />}
      {state === 'pinned' ? 'Saved' : state === 'pinning' ? 'Saving…' : 'Pin'}
    </button>
  );
}

export default function HeterosisSection({ heterosis, designKey = 'diallel', analysisId, analysisType, trait }) {
  if (!heterosis || !heterosis.rows || !heterosis.rows.length) return null;

  // Method 4 / L×T-without-parent-means cases have no MPH/BPH;
  // detect this and hide those columns rather than show a sea of "—".
  const hasMP = heterosis.rows.some((r) => r.MP !== null && r.MP !== undefined);
  const hasBP = heterosis.rows.some((r) => r.BP !== null && r.BP !== undefined);
  const cols = ['parents'];
  if (hasMP) cols.push('mph');
  if (hasBP) cols.push('bph');
  cols.push('sh');

  const sectionHead =
    'text-base font-bold text-[#1F4E2E] border-b-2 border-[#2D8B57] pb-1.5 mb-4 mt-8';

  return (
    <div className="space-y-4" data-testid={`heterosis-section-${designKey}`}>
      <h4 className={sectionHead} data-testid="heterosis-heading">
        Heterosis Analysis &mdash; MPH, BPH, SH
      </h4>

      <div className="rounded-lg border border-[#E0EDE0] bg-[#F4FAF5] p-3 px-4 text-[12px] leading-relaxed text-[#3C4043]">
        <strong className="text-[#1F4E2E]">Standard check:</strong>{' '}
        {fmtNum(heterosis.standard_check_used)}
        <span className="text-[#6B8F71] ml-1">({heterosis.standard_check_source})</span>
        {' · '}
        <strong className="text-[#1F4E2E]">Critical t:</strong>{' '}
        t<sub>0.05</sub> = {fmtNum(heterosis.critical_t_05, 3)},
        t<sub>0.01</sub> = {fmtNum(heterosis.critical_t_01, 3)}
        {' · '}
        <span className="font-mono">**</span> p&lt;0.01,{' '}
        <span className="font-mono">*</span> p&lt;0.05
      </div>

      {/* Top-5 cards row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="top5-row">
        {hasMP && (
          <TopCrossesCard
            title="Top 5 — Mid-Parent Heterosis (MPH)"
            rows={heterosis.top5_mph}
            metric="mph"
            accent="#2D8B57"
          />
        )}
        {hasBP && (
          <TopCrossesCard
            title="Top 5 — Better-Parent Heterosis (BPH)"
            rows={heterosis.top5_bph}
            metric="bph"
            accent="#0B5132"
          />
        )}
        <TopCrossesCard
          title="Top 5 — Standard Heterosis (SH)"
          rows={heterosis.top5_sh}
          metric="sh"
          accent="#7C3AED"
        />
      </div>

      {/* Mobile scroll hint */}
      <div className="md:hidden text-[10.5px] italic text-[#6B8F71] flex items-center gap-1.5">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
        Scroll the table sideways to see all heterosis columns
      </div>

      {/* Full table */}
      <div className="border border-[#E0EDE0] rounded-xl overflow-hidden bg-white" data-testid="heterosis-table-wrap">
        <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
          <table className="w-full border-collapse min-w-[640px]" data-testid="heterosis-table">
            <thead>
              <tr className="bg-[#F4FAF5] text-[#1F4E2E]">
                <th className="px-3 py-2.5 text-left text-[12px] font-bold">Cross</th>
                <th className="px-3 py-2.5 text-center text-[12px] font-bold">F1 mean</th>
                {cols.includes('parents') && (
                  <>
                    <th className="px-3 py-2.5 text-center text-[12px] font-bold text-[#6B8F71]">P1 mean</th>
                    <th className="px-3 py-2.5 text-center text-[12px] font-bold text-[#6B8F71]">P2 mean</th>
                  </>
                )}
                {cols.includes('mph') && (
                  <th className="px-3 py-2.5 text-center text-[12px] font-bold">
                    MPH<br /><span className="text-[10px] font-normal text-[#6B8F71]">vs (P1+P2)/2</span>
                  </th>
                )}
                {cols.includes('bph') && (
                  <th className="px-3 py-2.5 text-center text-[12px] font-bold">
                    BPH<br /><span className="text-[10px] font-normal text-[#6B8F71]">vs better parent</span>
                  </th>
                )}
                {cols.includes('sh') && (
                  <th className="px-3 py-2.5 text-center text-[12px] font-bold">
                    SH<br /><span className="text-[10px] font-normal text-[#6B8F71]">vs check</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {heterosis.rows.map((row) => (
                <HeterosisRow key={row.Cross} row={row} columns={cols} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* References */}
      <div className="text-[11px] italic text-[#6B8F71] leading-relaxed pt-2">
        {heterosis.reference_notes}
      </div>

      {/* Combined Selection Table — Mean × GCA × SCA × MPH × BPH × SH.
          Niranjan Phase A enhancement, Feb 2026. */}
      <CombinedSelectionTable
        selection={heterosis.combined_selection}
        analysisId={analysisId}
        analysisType={analysisType}
        trait={trait}
      />
    </div>
  );
}

function CombinedSelectionTable({ selection, analysisId, analysisType, trait }) {
  if (!selection || !selection.rows || !selection.rows.length) return null;
  const { rows, top_combined, has_gca, has_sca, flag_threshold, description } = selection;

  // Per-flag micro-pill styling — green if cross is in the top-N
  // for that metric, neutral otherwise.
  const pill = (active, label, key) => {
    const colors = {
      mean: '#0B5132', gca: '#1F5BA6', sca: '#7C3AED',
      mph: '#2D8B57', bph: '#0B5132', sh:  '#9F1239',
    };
    return (
      <span
        key={key}
        className="inline-flex items-center justify-center rounded-full"
        style={{
          width: 22, height: 18, fontSize: 9.5, fontWeight: 700,
          marginRight: 3,
          color: active ? 'white' : '#CBD0CB',
          background: active ? colors[key] : '#F0F2F0',
          border: active ? `1px solid ${colors[key]}` : '1px solid #E0EDE0',
          letterSpacing: '0.04em',
        }}
        title={`${label}: ${active ? 'in top ' + flag_threshold : 'not in top'}`}
      >
        {label}
      </span>
    );
  };

  const numCell = (v, isPct = false, isHigh = false) => {
    if (v === null || v === undefined) return <span className="text-[#CCC]">—</span>;
    const formatted = isPct ? `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` : Number(v).toFixed(2);
    const style = isHigh
      ? { color: '#0B5132', fontWeight: 700, background: '#D6F4E1', padding: '1px 6px', borderRadius: 4 }
      : {};
    return <span style={style}>{formatted}</span>;
  };

  return (
    <div className="space-y-4 mt-8" data-testid="combined-selection-section">
      <h4 className="text-base font-bold text-[#1F4E2E] border-b-2 border-[#7C3AED] pb-1.5 mb-4">
        Combined Selection Table &mdash; Mean &times; GCA &times; SCA &times; Heterosis
      </h4>
      <div className="rounded-lg bg-[#FAF8FF] border border-[#E2D6F5] p-3 px-4 text-[12px] leading-relaxed text-[#3C4043]">
        <strong className="text-[#5B21B6]">How to read this:</strong> {description}
      </div>

      {/* Top combined */}
      {top_combined && top_combined.length > 0 && (
        <div className="rounded-xl border border-[#E2D6F5] bg-white p-4" data-testid="top-combined-card">
          <h5 className="text-[13px] font-bold text-[#5B21B6] mb-3">
            Top {flag_threshold} candidates (by combined score)
          </h5>
          <ol className="space-y-2 text-[12px]">
            {top_combined.map((r, i) => (
              <li key={r.Cross} className="flex items-center gap-3 py-1 border-b border-[#F0EAFA] last:border-0">
                <span className="font-bold text-[#7C3AED] w-5 shrink-0">{i + 1}.</span>
                <span className="font-mono text-[#1F4E2E] font-semibold w-24 shrink-0">{r.Cross}</span>
                <span className="text-[#3C4043]">
                  {pill(r.flags.mean, 'M', 'mean')}
                  {has_gca && pill(r.flags.gca, 'G', 'gca')}
                  {has_sca && pill(r.flags.sca, 'S', 'sca')}
                  {pill(r.flags.mph, 'h', 'mph')}
                  {pill(r.flags.bph, 'b', 'bph')}
                  {pill(r.flags.sh, 's', 'sh')}
                </span>
                <span className="ml-auto text-[#5B21B6] font-bold font-mono whitespace-nowrap">
                  {r.top_score} / {2 + (has_gca ? 1 : 0) + (has_sca ? 1 : 0) + 3}
                </span>
                <PinCandidateButton
                  candidate={r}
                  analysisId={analysisId}
                  analysisType={analysisType}
                  trait={trait}
                />
              </li>
            ))}
          </ol>
          <div className="mt-3 pt-2 border-t border-[#F0EAFA] text-[10.5px] text-[#6B8F71] font-mono">
            <strong>Pills:</strong> M=top mean &middot;{' '}
            {has_gca && (<>G=top GCA(P1+P2) &middot; </>)}
            {has_sca && (<>S=top SCA &middot; </>)}
            h=top MPH &middot; b=top BPH &middot; s=top SH
          </div>
        </div>
      )}

      {/* Full combined table */}
      <div className="border border-[#E0EDE0] rounded-xl overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" data-testid="combined-selection-table">
            <thead>
              <tr className="bg-[#FAF8FF] text-[#5B21B6]">
                <th className="px-3 py-2.5 text-left text-[11px] font-bold">Cross</th>
                <th className="px-3 py-2.5 text-center text-[11px] font-bold">F1 mean</th>
                {has_gca && (
                  <>
                    <th className="px-3 py-2.5 text-center text-[11px] font-bold">GCA P1</th>
                    <th className="px-3 py-2.5 text-center text-[11px] font-bold">GCA P2</th>
                    <th className="px-3 py-2.5 text-center text-[11px] font-bold">GCA<br /><span className="text-[9.5px] font-normal">(P1+P2)</span></th>
                  </>
                )}
                {has_sca && (<th className="px-3 py-2.5 text-center text-[11px] font-bold">SCA</th>)}
                <th className="px-3 py-2.5 text-center text-[11px] font-bold">MPH%</th>
                <th className="px-3 py-2.5 text-center text-[11px] font-bold">BPH%</th>
                <th className="px-3 py-2.5 text-center text-[11px] font-bold">SH%</th>
                <th className="px-3 py-2.5 text-center text-[11px] font-bold">Score</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.Cross} className="hover:bg-[#FAF8FF] border-t border-[#F0F2F0]">
                  <td className="px-3 py-2 font-mono text-[12px] text-[#1F4E2E] font-semibold">{r.Cross}</td>
                  <td className="px-3 py-2 text-center font-mono text-[12px]">{numCell(r.F1_mean, false, r.flags.mean)}</td>
                  {has_gca && (
                    <>
                      <td className="px-3 py-2 text-center font-mono text-[11px] text-[#6B8F71]">{numCell(r.GCA_P1)}</td>
                      <td className="px-3 py-2 text-center font-mono text-[11px] text-[#6B8F71]">{numCell(r.GCA_P2)}</td>
                      <td className="px-3 py-2 text-center font-mono text-[12px]">{numCell(r.GCA_sum, false, r.flags.gca)}</td>
                    </>
                  )}
                  {has_sca && (<td className="px-3 py-2 text-center font-mono text-[12px]">{numCell(r.SCA, false, r.flags.sca)}</td>)}
                  <td className="px-3 py-2 text-center font-mono text-[12px]">{numCell(r.MPH_pct, true, r.flags.mph)}{r.MPH_sig && <sup className="ml-0.5">{r.MPH_sig}</sup>}</td>
                  <td className="px-3 py-2 text-center font-mono text-[12px]">{numCell(r.BPH_pct, true, r.flags.bph)}{r.BPH_sig && <sup className="ml-0.5">{r.BPH_sig}</sup>}</td>
                  <td className="px-3 py-2 text-center font-mono text-[12px]">{numCell(r.SH_pct, true, r.flags.sh)}{r.SH_sig && <sup className="ml-0.5">{r.SH_sig}</sup>}</td>
                  <td className="px-3 py-2 text-center font-mono text-[12px] font-bold text-[#5B21B6]">{r.top_score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
